/* eslint-disable */
import { useEffect, useMemo, useReducer, useState } from 'react';
import { connect } from 'react-redux';
import {
	TextField,
	Button,
	Switch,
	IconButton,
	FormControlLabel,
	Chip,
	Tooltip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from '@mui/material';
import { Trash2, Plus, Users, QrCode, Pencil } from 'lucide-react';
import RetailFloorConfig, { floorReducer } from './RetailFloor.config';
import { formActions } from '../../Actions';
import { CreatePageHeader } from '../../Components/Headers';
import { BackdropLoader } from '../../Components/Loaders';
import { notificationServices } from '../../Services';
import retailFloorServices from '../../Services/retailFloor.services';
import StorePicker from '../../Components/StorePicker/StorePicker';

const UNSECTIONED = '';

// ---- Add-section modal ------------------------------------------------------
const AddSectionDialog = ({ open, onClose, onAdd }) => {
	const [name, setName] = useState('');
	useEffect(() => {
		if (open) setName('');
	}, [open]);
	const submit = () => {
		if (name.trim()) onAdd(name.trim());
	};
	return (
		<Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
			<DialogTitle sx={{ fontSize: 16 }}>Add section</DialogTitle>
			<DialogContent dividers sx={{ pt: 2 }}>
				<TextField
					size='small'
					fullWidth
					label='Section name'
					placeholder='e.g. Ground floor'
					autoFocus
					value={name}
					onChange={(e) => setName(e.target.value)}
					onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), submit())}
				/>
			</DialogContent>
			<DialogActions>
				<Button color='inherit' onClick={onClose}>
					Cancel
				</Button>
				<Button variant='contained' disabled={!name.trim()} onClick={submit}>
					Add
				</Button>
			</DialogActions>
		</Dialog>
	);
};

// ---- Add-table modal --------------------------------------------------------
const AddTableDialog = ({ open, section, onClose, onAdd }) => {
	const [name, setName] = useState('');
	const [capacity, setCapacity] = useState(2);
	useEffect(() => {
		if (open) {
			setName('');
			setCapacity(2);
		}
	}, [open]);
	return (
		<Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
			<DialogTitle sx={{ fontSize: 16 }}>
				Add table
				<div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>
					{section || 'Unsectioned'}
				</div>
			</DialogTitle>
			<DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
				<TextField
					size='small'
					label='Table name'
					placeholder='T-01'
					autoFocus
					value={name}
					onChange={(e) => setName(e.target.value)}
				/>
				<TextField
					size='small'
					label='Seats (pax)'
					type='number'
					value={capacity}
					onChange={(e) => setCapacity(e.target.value)}
					inputProps={{ min: 0 }}
				/>
			</DialogContent>
			<DialogActions>
				<Button color='inherit' onClick={onClose}>
					Cancel
				</Button>
				<Button
					variant='contained'
					disabled={!name.trim()}
					onClick={() => onAdd({ name: name.trim(), capacity: Number(capacity) || 0 })}
				>
					Add
				</Button>
			</DialogActions>
		</Dialog>
	);
};

// ---- Edit-table modal -------------------------------------------------------
const EditTableDialog = ({ open, table, onClose, onSave, onDelete }) => {
	const [draft, setDraft] = useState(table || {});
	useEffect(() => setDraft(table || {}), [table]);
	if (!table) return null;
	const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
	return (
		<Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
			<DialogTitle sx={{ fontSize: 16 }}>Edit table</DialogTitle>
			<DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
				<TextField
					size='small'
					label='Table name'
					value={draft.name || ''}
					onChange={(e) => set('name', e.target.value)}
				/>
				<TextField
					size='small'
					label='Seats (pax)'
					type='number'
					value={draft.capacity ?? ''}
					onChange={(e) => set('capacity', e.target.value)}
					inputProps={{ min: 0 }}
				/>
				<FormControlLabel
					control={
						<Switch
							size='small'
							checked={!!draft.isEnabled}
							onChange={(e) => set('isEnabled', e.target.checked)}
						/>
					}
					label='Enabled'
				/>
			</DialogContent>
			<DialogActions sx={{ justifyContent: 'space-between' }}>
				<Button color='error' startIcon={<Trash2 size={15} />} onClick={onDelete}>
					Remove
				</Button>
				<div>
					<Button color='inherit' onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant='contained'
						onClick={() =>
							onSave({ name: draft.name, capacity: draft.capacity, isEnabled: draft.isEnabled })
						}
					>
						Save
					</Button>
				</div>
			</DialogActions>
		</Dialog>
	);
};

// ---- Single table card ------------------------------------------------------
const TableCard = ({ table, tableIndex, onToggleOccupied, onEdit }) => (
	<div
		style={{
			position: 'relative',
			border: '1px solid #e5e7eb',
			borderRadius: 12,
			padding: 14,
			minWidth: 190,
			opacity: table.isEnabled ? 1 : 0.55,
		}}
	>
		<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
			<span style={{ fontSize: 16, fontWeight: 700, color: '#111827', flex: 1 }}>
				{table.name || 'Table'}
			</span>
			<Tooltip title='QR link is generated at runtime'>
				<span style={{ color: '#9ca3af', display: 'inline-flex' }}>
					<QrCode size={16} />
				</span>
			</Tooltip>
			<IconButton size='small' onClick={() => onEdit(tableIndex)} sx={{ p: 0.3 }}>
				<Pencil size={13} />
			</IconButton>
		</div>
		<div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#9ca3af', fontSize: 13, margin: '4px 0 10px' }}>
			<Users size={13} /> {table.capacity} pax
		</div>
		<Chip
			size='small'
			label={
				!table.id
					? 'New'
					: table.occupied
					? 'Occupied'
					: 'Free'
			}
			color={!table.id ? 'default' : table.occupied ? 'warning' : 'success'}
			variant='outlined'
			onClick={table.id ? () => onToggleOccupied(table, tableIndex) : undefined}
			sx={{ cursor: table.id ? 'pointer' : 'default', fontWeight: 600 }}
		/>
	</div>
);

const RetailFloorPage = (props) => {
	const { fetchStores, storesList } = props;
	const [storeId, setStoreId] = useState('');
	const [state, dispatch] = useReducer(floorReducer, RetailFloorConfig.initialState);
	const [isLoading, setIsLoading] = useState(false);
	const [addSectionOpen, setAddSectionOpen] = useState(false);
	const [addFor, setAddFor] = useState(null); // section label the add-table modal targets
	const [editIdx, setEditIdx] = useState(null);

	useEffect(() => {
		fetchStores({ entity: 'STORES', req: { params: { id: '' } }, method: 'LIST' });
	}, []);

	const loadFloor = async (sid) => {
		if (!sid) return;
		setIsLoading(true);
		try {
			const res = await retailFloorServices.getFloor({ storeId: sid });
			if (res?.statusCode === 200) dispatch({ type: 'INIT', payload: res.data });
			else
				notificationServices.generateNotification({
					type: 'error',
					message: res?.message || 'Could not load floor',
				});
		} catch (e) {
			notificationServices.generateNotification({ type: 'error', message: 'Could not load floor' });
		} finally {
			setIsLoading(false);
		}
	};

	const handleStoreChange = (sid) => {
		setStoreId(sid);
		dispatch({ type: 'RESET' });
		loadFloor(sid);
	};

	const handleToggleOccupied = async (table, tableIndex) => {
		try {
			const res = await retailFloorServices.updateTableStatus({
				tableId: table.id,
				data: { occupied: !table.occupied },
			});
			if (res?.statusCode === 200) {
				dispatch({ type: 'UPDATE_TABLE', tableIndex, key: 'occupied', value: !table.occupied });
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: res?.message || 'Could not update table',
				});
			}
		} catch (e) {
			notificationServices.generateNotification({ type: 'error', message: 'Could not update table' });
		}
	};

	// section label -> [{t, idx}] with flat index preserved
	const groups = useMemo(() => {
		const bySection = {};
		(state.tables || []).forEach((t, idx) => {
			const key = t.section || UNSECTIONED;
			(bySection[key] = bySection[key] || []).push({ t, idx });
		});
		const ordered = (state.sections || []).map((s) => ({ section: s, entries: bySection[s] || [] }));
		if (bySection[UNSECTIONED]) ordered.push({ section: UNSECTIONED, entries: bySection[UNSECTIONED] });
		return ordered;
	}, [state.tables, state.sections]);

	const addSection = (name) => {
		const n = (name || '').trim();
		if (!n) return;
		dispatch({ type: 'ADD_SECTION', name: n });
		setAddSectionOpen(false);
	};

	const handleSave = async () => {
		if (!storeId) {
			notificationServices.generateNotification({ type: 'error', message: 'Pick a store first' });
			return;
		}
		if ((state.tables || []).some((t) => !t.name.trim())) {
			notificationServices.generateNotification({ type: 'error', message: 'Every table needs a name.' });
			return;
		}
		setIsLoading(true);
		try {
			const res = await retailFloorServices.saveFloor({
				storeId,
				data: RetailFloorConfig.toPayload(state),
			});
			if (res?.statusCode === 200) {
				notificationServices.generateNotification({ type: 'success', message: 'Floor saved' });
				dispatch({ type: 'INIT', payload: res.data });
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: res?.message || 'Could not save floor',
				});
			}
		} catch (e) {
			notificationServices.generateNotification({ type: 'error', message: 'Could not save floor' });
		} finally {
			setIsLoading(false);
		}
	};

	const storeName = (storesList || []).find((s) => s.id === storeId)?.name;
	const editTable = editIdx !== null ? state.tables[editIdx] : null;

	return (
		<div className='root' style={{ paddingBottom: 40 }}>
			<BackdropLoader showProgress isLoading={isLoading} />
			<CreatePageHeader
				heading='Floor & tables'
				showBreadcrumbs
				breadcrumbs={[
					{ link: '', name: 'Outlets' },
					{ link: '', name: storeName || 'Select store' },
					{ link: '', name: 'Floor & tables' },
				]}
				showCancel={false}
			/>

			<div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
					<div style={{ minWidth: 280 }}>
						<StorePicker
							stores={storesList || []}
							value={storeId}
							onChange={(id) => handleStoreChange(id)}
							label='Store'
						/>
					</div>
					{storeId && (
						<Button
							variant='outlined'
							startIcon={<Plus size={16} />}
							onClick={() => setAddSectionOpen(true)}
							sx={{ textTransform: 'none' }}
						>
							Add section
						</Button>
					)}
					<div style={{ flex: 1 }} />
					{storeId && (
						<Button variant='contained' onClick={handleSave} sx={{ textTransform: 'none' }}>
							Save floor
						</Button>
					)}
				</div>

				<div style={{ maxHeight: 'calc(100vh - 230px)', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
				{!storeId ? (
					<div style={{ color: '#9ca3af', fontSize: 14, textAlign: 'center', padding: '48px 0' }}>
						Pick a store to manage its floor.
					</div>
				) : groups.length === 0 ? (
					<div style={{ color: '#9ca3af', fontSize: 14, textAlign: 'center', padding: '48px 0' }}>
						No sections yet — use <b>Add section</b> to create one, then add tables.
					</div>
				) : (
					<>
						{groups.map((group) => (
							<div
								key={group.section || '__unsectioned'}
								style={{ border: '1px solid #e5e7eb', borderRadius: 14, padding: 16 }}
							>
								<div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
									<div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
										{group.section || 'Unsectioned'}
										<span style={{ fontSize: 13, color: '#9ca3af', fontWeight: 400 }}>
											{'  '}· {group.entries.length} tables
										</span>
									</div>
									<div style={{ flex: 1 }} />
									<Button
										size='small'
										startIcon={<Plus size={15} />}
										onClick={() => setAddFor(group.section)}
										sx={{ textTransform: 'none' }}
									>
										Table
									</Button>
									{group.section ? (
										<Tooltip title='Remove section (and its tables)'>
											<IconButton
												size='small'
												onClick={() => dispatch({ type: 'REMOVE_SECTION', name: group.section })}
											>
												<Trash2 size={15} />
											</IconButton>
										</Tooltip>
									) : null}
								</div>

								<div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
									{group.entries.map(({ t, idx }) => (
										<TableCard
											key={t._key}
											table={t}
											tableIndex={idx}
											onToggleOccupied={handleToggleOccupied}
											onEdit={setEditIdx}
										/>
									))}
									<div
										onClick={() => setAddFor(group.section)}
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											gap: 6,
											minWidth: 190,
											border: '1px dashed #d1d5db',
											borderRadius: 12,
											color: '#6b7280',
											cursor: 'pointer',
											padding: 14,
										}}
									>
										<Plus size={16} /> Add table
									</div>
								</div>
							</div>
						))}
					</>
				)}
				</div>
			</div>

			<AddSectionDialog
				open={addSectionOpen}
				onClose={() => setAddSectionOpen(false)}
				onAdd={addSection}
			/>

			<AddTableDialog
				open={addFor !== null}
				section={addFor}
				onClose={() => setAddFor(null)}
				onAdd={({ name, capacity }) => {
					dispatch({ type: 'ADD_TABLE', section: addFor, name, capacity });
					setAddFor(null);
				}}
			/>

			<EditTableDialog
				open={editIdx !== null}
				table={editTable}
				onClose={() => setEditIdx(null)}
				onSave={(patch) => {
					dispatch({ type: 'UPDATE_TABLE', tableIndex: editIdx, patch });
					setEditIdx(null);
				}}
				onDelete={() => {
					dispatch({ type: 'REMOVE_TABLE', tableIndex: editIdx });
					setEditIdx(null);
				}}
			/>
		</div>
	);
};

const mapStateToProps = (state) => ({
	storesList: state.form.STORES.data.LIST,
});

const mapDispatchToProps = {
	fetchStores: formActions.gateway,
};

export default connect(mapStateToProps, mapDispatchToProps)(RetailFloorPage);
